import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import generateAvatar from '../../utils/generateAvatar';
import './embed.css';

const EmbedRecentTrades = () => {
  const [searchParams] = useSearchParams();

  // URL params for customization
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || (theme === 'dark' ? '#0a0a0a' : '#ffffff');
  const limit = parseInt(searchParams.get('limit')) || 10;
  const showHeader = searchParams.get('header') !== 'false';
  const ticker = searchParams.get('ticker');
  const userId = searchParams.get('userId');
  const accentColor = searchParams.get('accent') || '#00ff88';
  const refreshInterval = parseInt(searchParams.get('refresh')) || 5000;
  const animate = searchParams.get('animate') !== 'false';
  const style = searchParams.get('style') || 'feed'; // feed, compact, minimal

  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockMap, setStockMap] = useState({});
  const socketRef = useRef(null);
  const prevTradeIdsRef = useRef(new Set());

  const fetchStocks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stocks`);
      if (response.ok) {
        const data = await response.json();
        const stocks = Array.isArray(data) ? data : (data.stocks || []);
        const map = {};
        stocks.forEach(s => {
          map[s.id] = { ticker: s.ticker, name: s.name };
          map[s.ticker] = { id: s.id, name: s.name };
        });
        setStockMap(map);
      }
    } catch (error) {
      console.error('Error fetching stocks:', error);
    }
  };

  const fetchTrades = async () => {
    try {
      let url = `${API_BASE_URL}/api/trades/recent?limit=${limit}`;
      if (ticker) url += `&ticker=${ticker}`;
      if (userId) url += `&userId=${userId}`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const allTrades = Array.isArray(data) ? data : (data.trades || []);

        const sortedTrades = allTrades
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .slice(0, limit);

        const currentIds = new Set(sortedTrades.map(t => t.id || `${t.timestamp}-${t.stockId}`));
        sortedTrades.forEach(trade => {
          const tradeId = trade.id || `${trade.timestamp}-${trade.stockId}`;
          trade.isNew = !prevTradeIdsRef.current.has(tradeId);
        });
        prevTradeIdsRef.current = currentIds;

        setTrades(sortedTrades);
      }
    } catch (error) {
      console.error('Error fetching trades:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
  }, []);

  useEffect(() => {
    fetchTrades();

    socketRef.current = io(API_BASE_URL, { auth: { public: true } });

    socketRef.current.on('trade-executed', (data) => {
      if (ticker && data.ticker?.toUpperCase() !== ticker.toUpperCase()) return;
      if (userId && data.userId !== userId) return;

      setTrades(prev => {
        const newTrade = { ...data, isNew: true, timestamp: data.timestamp || new Date().toISOString() };
        return [newTrade, ...prev.slice(0, limit - 1)];
      });
    });

    const interval = setInterval(fetchTrades, refreshInterval);

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      clearInterval(interval);
    };
  }, [ticker, userId, limit, refreshInterval]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getActionVerb = (type, tradeType) => {
    if (tradeType === 'TRADE_CLOSE' || type === 'CLOSE_LONG' || type === 'CLOSE_SHORT') return 'closed';
    return type === 'LONG' ? 'went long on' : 'shorted';
  };



  if (loading) {
    return (
      <div className="embed-container embed-trades-feed" style={{ background: bg }}>
        <div className="embed-loading">Loading trades...</div>
      </div>
    );
  }

  return (
    <div
      className={`embed-container embed-trades-feed ${theme} style-${style}`}
      style={{ background: bg === 'transparent' ? 'transparent' : bg }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {showHeader && (
        <div className="feed-header">
          <span className="feed-title">
            {ticker ? `${ticker} ACTIVITY` : userId ? 'TRADER ACTIVITY' : 'LIVE TRADES'}
          </span>
          <span className="feed-live-dot"></span>
        </div>
      )}

      <div className="feed-list">
        {trades.length === 0 ? (
          <div className="feed-empty">No recent activity</div>
        ) : (
          trades.map((trade, index) => {
            const tradeId = trade.id || `${index}-${trade.timestamp}`;
            const isLong = trade.type === 'LONG' || trade.type === 'BUY' || trade.type === 'CLOSE_LONG';
            const isClose = trade.tradeType === 'TRADE_CLOSE' || trade.type === 'CLOSE_LONG' || trade.type === 'CLOSE_SHORT';
            const stockTicker = trade.ticker || '???';
            const stockInfo = stockMap[stockTicker] || {};
            const actionVerb = getActionVerb(trade.type, trade.tradeType);

            return (
              <div
                key={tradeId}
                className={`feed-item ${animate && trade.isNew ? 'feed-item-new' : ''}`}
              >
                {/* Avatar */}
                <div className="feed-avatar">
                  <img src={trade.userAvatar || generateAvatar(trade.username)} alt="" className="avatar-img" />
                </div>

                {/* Content */}
                <div className="feed-content">
                  <div className="feed-text">
                    <span className="feed-username">{trade.username || 'Trader'}</span>
                    <span className="feed-action-text">{actionVerb}</span>
                    <span className={`feed-stock ${isClose ? 'close' : (isLong ? 'long' : 'short')}`}>
                      ${stockTicker}
                    </span>
                  </div>

                  <div className="feed-meta">
                    <span className={`feed-badge ${isClose ? 'close' : (isLong ? 'long' : 'short')}`}>
                      {isClose ? 'CLOSED' : (isLong ? 'LONG' : 'SHORT')}
                    </span>
                    <span className="feed-price">{formatCurrency(trade.price)}</span>
                    {trade.shares > 1 && <span className="feed-shares">×{trade.shares}</span>}
                    <span className="feed-time">{formatTime(trade.timestamp)}</span>
                  </div>
                </div>

                {/* Action indicator */}
                <div className={`feed-indicator ${isClose ? 'close' : (isLong ? 'long' : 'short')}`}>
                  {isClose ? '✕' : (isLong ? '▲' : '▼')}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EmbedRecentTrades;
